import { Checkbox } from '../../../../../../../widgets/checkbox';
import { Dialog } from '../../../../../../../widgets/dialog';
import { FormControl } from '../../../../../../../widgets/formControl';
import { Input } from '../../../../../../../widgets/input';
import { Link } from '../../../../../../../widgets/buttons';
import { Select as SingleSelect } from '../../../../../../../widgets/select';
import React, { useEffect } from 'react';
import Select from 'react-select';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { ComposerAreaNode } from '../../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { getAreaPath, getAreaSVG } from '../../helpers/composerHelpers';
import { ComposerTemplateNodeAdapter } from '../../helpers/composerNodesAdapter';

const ComposerSaveOptionDialogWindow = styled(Dialog)`
    max-width: 600px;
`;

const ComposerSaveAreaContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    max-height: 600px;
    overflow-y: auto;
    align-items: center;
    padding: 2em;
    justify-content: start;
`;

const FormInline = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: center;
    width: 100%;
    gap: 15px;
`;

interface ComposerSaveAreaProps {
    onConfirm: (option: ComposerAreaNode | null) => void;
    passedArea?: ComposerAreaNode | null;
    meshesList: BABYLON.AbstractMesh[];
    camerasList: BABYLON.Camera[];
    edit?: boolean;
}

const ComposerSaveAreaDialog: React.FC<ComposerSaveAreaProps> = ({ onConfirm, passedArea, edit = false, meshesList, camerasList }) => {
    const [parsedArea, setParsedArea] = React.useState<{ side: MPlaza.Side; modelSides: MPlaza.ModelSceneMeshSides }>(
        ComposerTemplateNodeAdapter.fromAreaNode(passedArea || null)
    );
    const [size, setSize] = React.useState<{ width: number; height: number }>({
        width: 0,
        height: 0
    });

    const handleConfirm = () => {
        console.log('CONFIRM AREA', parsedArea, ComposerTemplateNodeAdapter.toNodeArea(parsedArea.side, parsedArea.modelSides));
        onConfirm(ComposerTemplateNodeAdapter.toNodeArea(parsedArea.side, parsedArea.modelSides));
    };

    useEffect(() => {
        console.log('PASSED AREA', passedArea);
        if (passedArea) {
            const parsedArea = ComposerTemplateNodeAdapter.fromAreaNode(passedArea);
            handleSizeChange(
                Math.round((parsedArea.side.get('areas').at(0).get('boundsWidth') / parsedArea.side.get('ppcm')) * 100) / 100 || 10,
                Math.round((parsedArea.side.get('areas').at(0).get('boundsHeight') / parsedArea.side.get('ppcm')) * 100) / 100 || 10
            );
            update();
        } else {
            handleSizeChange(10, 10);
        }
    }, [passedArea]);

    const handleSizeChange = (width: number, height: number) => {
        setSize({ width, height });
        let ratio = width / height;
        let areaWidth = 1024;
        let areaHeight = 1024 / ratio;

        parsedArea.side.set('ppcm', 1024 / width);
        parsedArea.side.get('areas').at(0).set('boundsWidth', areaWidth);
        parsedArea.side.get('areas').at(0).set('boundsHeight', areaHeight);
        (parsedArea.side.get('areas').at(0) as any).set({
            path: JSON.stringify(getAreaPath(areaWidth, areaHeight)),
            pathSVG: getAreaSVG(areaWidth, areaHeight),
            boundsX: -areaWidth / 2,
            boundsY: -areaHeight / 2,
            boundsWidth: areaWidth,
            boundsHeight: areaHeight
        });
    };

    const update = () => {
        setParsedArea((prev) => ({
            ...prev
        }));
    };

    const title = edit ? T._('Edit Area', 'Admin') : T._('Create New Area', 'Admin');

    const [hasCustomTextureResolution, customDesktopTextureResolution, customMobileTextureResolution] = React.useMemo(() => {
        const desktopResolution = parsedArea.modelSides?.at(0)?.getConfig()?.getGeneralConfig()?.get('desktopTextureResolutionWidth') ?? false;
        const mobileResolution = parsedArea.modelSides?.at(0)?.getConfig()?.getGeneralConfig()?.get('mobileTextureResolutionWidth') ?? false;

        return [!!desktopResolution || !!mobileResolution, desktopResolution, mobileResolution];
    }, [parsedArea.modelSides]);
    return (
        <Dialog title={title} windowDecorator={ComposerSaveOptionDialogWindow} buttons={[{ label: T._('Confirm', 'Admin'), onClick: handleConfirm }]}>
            <ComposerSaveAreaContainer>
                <div>
                    {T._('Do you need help?', 'Admin')}{' '}
                    <Link
                        href='https://zakeke.zendesk.com/hc/en-us/articles/12934839221020-Attributes-Options-and-Actions#h_01G0MHCK9C0KKE90D04WE4SVM4'
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {T._('Read here', 'Admin')}
                    </Link>
                </div>
                <FormControl label={T._('Area Name', 'Admin')}>
                    <Input
                        value={parsedArea.side.get('name') || ''}
                        onChange={(e) => {
                            parsedArea.side.set('name', e.target.value);
                            update();
                        }}
                    />
                </FormControl>
                <FormControl label={T._('Associated Meshes', 'Admin')}>
                    <Select
                        isMulti
                        options={
                            meshesList?.map((mesh) => ({
                                value: mesh.id,
                                label: mesh.name || mesh.id
                            })) ?? []
                        }
                        value={
                            parsedArea.modelSides
                                ?.map((m) => m.get('meshID'))
                                ?.map((s) => ({ value: s, label: meshesList?.find((m) => m.id === s)?.name || s })) || []
                        }
                        onChange={(selected) => {
                            parsedArea.modelSides = new MPlaza.ModelSceneMeshSides();
                            selected.forEach((s) => {
                                let currentModelSide = parsedArea.modelSides.find((m) => m.get('meshID') === s.value);
                                if (!currentModelSide) {
                                    currentModelSide = new MPlaza.ModelSceneMeshSide();
                                    currentModelSide.set('meshID', s.value);
                                    currentModelSide.set('sideID', parsedArea.side.get('sideID'));
                                }
                                parsedArea.modelSides.push(currentModelSide);
                            });
                            update();
                        }}
                    />
                </FormControl>
                <FormControl label={T._('Area size', 'Admin')}>
                    <FormInline>
                        <Input
                            value={size.width}
                            onChange={(e) => {
                                handleSizeChange(Number(e.target.value), size.height);
                                update();
                            }}
                        />
                        X
                        <Input
                            value={size.height}
                            onChange={(e) => {
                                handleSizeChange(size.width, Number(e.target.value));
                                update();
                            }}
                        />
                    </FormInline>
                </FormControl>
                <FormControl label={T._('Camera position', 'Admin')}>
                    <SingleSelect
                        value={camerasList?.find((camera) => camera.id === (parsedArea.side as any).get('cameraLocationID'))?.name || ''}
                        onChange={(selected) => {
                            (parsedArea.side as any).set('cameraLocationID', selected);
                            update();
                        }}
                    >
                        {camerasList &&
                            camerasList
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((camera) => (
                                    <option key={camera.id} value={camera.id}>
                                        {camera.name || camera.id}
                                    </option>
                                ))}
                    </SingleSelect>
                </FormControl>
                <FormControl label={T._('UV Map Channel', 'Admin')}>
                    <Input
                        type='number'
                        value={parsedArea?.modelSides?.at(0)?.get('uvChannel') || 1}
                        onChange={(e) => {
                            const selected = Number(e.target.value);
                            parsedArea.modelSides.forEach((side) => {
                                side.set('uvChannel', selected);
                            });
                            update();
                        }}
                    />
                </FormControl>
                <FormControl
                    label={T._('Design texture resolution', 'Admin')}
                    tooltipLabelHtml={
                        <div
                            dangerouslySetInnerHTML={{
                                __html: T._(
                                    "When displaying a 2D design on the 3D model, a texture of the design is generated with default values of 1024 for desktop, 1024 for Android mobile devices, and 512 for iPhone devices to ensure good performance. However, this default resolution may result in some designs appearing blurry. If the default display is inadequate, you can specify custom values for the texture. Note: For iPhones, the resolution will always be set to 512 regardless of the value you enter in the 'Mobile Texture Size' field, as exceeding 512 may cause crashes on different iPhone models. We generally recommend not setting values higher than 2048 pixels for desktop and 1024 pixels for mobile.",
                                    'Admin'
                                )
                            }}
                        />
                    }
                >
                    <Checkbox
                        checked={!!hasCustomTextureResolution}
                        onChange={(e) => {
                            const selected = e;
                            parsedArea.modelSides.forEach((side) => {
                                const config = side?.getOrCreateConfig();
                                const generalConfig = config?.getOrCreateGeneralConfig();
                                (generalConfig as any)?.set('desktopTextureResolutionHeight', selected ? 1024 : undefined);
                                (generalConfig as any)?.set('desktopTextureResolutionWidth', selected ? 1024 : undefined);
                                (generalConfig as any)?.set('mobileTextureResolutionHeight', selected ? 512 : undefined);
                                (generalConfig as any)?.set('mobileTextureResolutionWidth', selected ? 512 : undefined);
                                config?.set('general', generalConfig);
                                side.set('config', config);
                            });
                            console.log('hasCustomTextureResolution', parsedArea);
                            update();
                        }}
                    />
                </FormControl>
                {hasCustomTextureResolution && (
                    <>
                        <FormControl label={T._('Desktop Texture Size (pixels)', 'Admin')}>
                            <Input
                                type='number'
                                value={(customDesktopTextureResolution || 1024) as number}
                                onChange={(e) => {
                                    const selected = Number(e.target.value);
                                    parsedArea.modelSides.forEach((side) => {
                                        const config = side?.getOrCreateConfig();
                                        const generalConfig = config?.getOrCreateGeneralConfig();
                                        generalConfig?.set('desktopTextureResolutionHeight', selected);
                                    });
                                    update();
                                }}
                            />
                        </FormControl>
                        <FormControl label={T._('Mobile Texture Size (pixels)', 'Admin')}>
                            <Input
                                type='number'
                                value={(customMobileTextureResolution || 512) as number}
                                onChange={(e) => {
                                    const selected = Number(e.target.value);
                                    parsedArea.modelSides.forEach((side) => {
                                        const config = side?.getOrCreateConfig();
                                        const generalConfig = config?.getOrCreateGeneralConfig();
                                        generalConfig?.set('mobileTextureResolutionHeight', selected);
                                    });
                                    update();
                                }}
                            />
                        </FormControl>
                    </>
                )}
            </ComposerSaveAreaContainer>
        </Dialog>
    );
};

export default ComposerSaveAreaDialog;
